import { useSafeState } from "ahooks";
import { useCallback, useEffect, useRef } from "react";

/**
 * 下拉内容点击内容外进行隐藏功能的封装
 * @typedef {import("react").MutableRefObject} MutableRefObject
 * @param {Boolean} initState 初始隐藏状态
 * @param {{
 *  action: string | 'click'
 *  isCapture: boolean | 'false'
 *  extraTargets: MutableRefObject[]
 * }} option 额外配置项
 * @returns {[ref, visible, setVisible]} [ref,visible, setVisible]
 * ref引用, 指向下拉框dom元素
 * visible： 下拉框显示隐藏变量
 * setVisible： 控制下拉框显示隐藏变量
 * extraTargets 额外点击不会隐藏的排除目标
 * @example const [ref, visible, setVisible] = useOuterClick(false);
 */
export default function useOuterClick(
  initState,
  { action = "click", isCapture = true, extraTargets = [] } = {}
) {
  const ref = useRef(null);
  const [visible, setVisible] = useSafeState(initState);

  const judgeState = useCallback(
    (e) => {
      if (!ref.current) return;

      let tag = ref.current.contains(e.target);

      // 额外点击不会隐藏的排除目标
      if (!tag) {
        for (let i = 0; i < extraTargets.length; i++) {
          const el = extraTargets[i].current;
          if (el) {
            tag = el.contains(e.target);
          }
        }
      }

      if (!tag) {
        document.removeEventListener(action, judgeState, {
          capture: isCapture,
        });
        setVisible(false);
      }
    },
    [action, extraTargets, isCapture, setVisible]
  );

  useEffect(() => {
    if (visible) {
      document.addEventListener(action, judgeState, { capture: isCapture });
      return () =>
        document.removeEventListener(action, judgeState, {
          capture: isCapture,
        }); //销毁阶段
    }
  }, [action, isCapture, judgeState, visible]);

  return [ref, visible, setVisible];
}
